import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Flame, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './ui/button';

interface YapFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyFilters: (sortBy: YapSortOption) => void;
    currentSort: YapSortOption;
}

import { type YapSortOption } from '../contexts/YapDayContext';

// Descriptions carry the time scope, since "Trending" and "Most Engagement"
// otherwise read as near-synonyms — the window is the actual difference.
const sortOptions: { value: YapSortOption; label: string; description: string; icon: any }[] = [
    {
        value: 'trending',
        label: 'Trending',
        description: 'Last 7 days · engagement + recency',
        icon: TrendingUp,
    },
    {
        value: 'most-engagement',
        label: 'Most Engagement',
        description: 'All time · pure engagement, no recency',
        icon: Flame,
    },
    {
        value: 'newest',
        label: 'Newest Yaps',
        description: 'Most recent first',
        icon: Clock,
    },
];

export function YapFilterModal({ isOpen, onClose, onApplyFilters, currentSort }: YapFilterModalProps) {
    const [selectedSort, setSelectedSort] = useState<YapSortOption>(currentSort);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    // Re-sync each time the sheet opens. The component stays mounted (it lives in
    // a portal), so without this the radio kept whatever was selected at first
    // mount — e.g. after "New Yaps Dropped!" switched the feed to Newest, or after
    // the 24h reset returned it to Trending, the menu still showed the old choice.
    useEffect(() => {
        if (isOpen) setSelectedSort(currentSort);
    }, [isOpen, currentSort]);

    const handleApply = () => {
        onApplyFilters(selectedSort);
        onClose();
    };

    const handleReset = () => {
        setSelectedSort('trending');
    };

    if (!mounted) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-100 flex items-end justify-center md:items-center px-4 pt-4 pb-24 md:p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className="relative z-10 w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-3xl overflow-hidden shadow-2xl"
                    >
                        {/* Header */}
                        <div className="p-6 flex items-center justify-between border-b border-black/10 dark:border-white/10">
                            <div>
                                <h2 className="text-2xl text-black dark:text-white font-bold">
                                    Sort Yaps
                                </h2>
                                <p className="text-sm text-[#373737] dark:text-[#E5E5E5]">
                                    Choose how to order yaps
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-[#666666] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Sort Options */}
                        <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
                            {sortOptions.map((option) => {
                                const Icon = option.icon;
                                const isSelected = selectedSort === option.value;

                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => setSelectedSort(option.value)}
                                        className={`w-full p-4 rounded-xl border-2 transition-all text-left ${isSelected
                                            ? 'border-[#F39C12] dark:border-[#4478ea] bg-[#F39C12]/10 dark:bg-[#4478ea]/10'
                                            : 'border-black/10 dark:border-white/10 bg-[#F5F5F5] dark:bg-[#2a2a2a] hover:border-[#F39C12]/50 dark:hover:border-[#4478ea]/50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                             <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected
                                                ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1]'
                                                : 'bg-black/5 dark:bg-white/5'
                                                }`}>
                                                <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-[#666666] dark:text-[#E5E5E5]'
                                                    }`} />
                                            </div>
                                            <div className="flex-1">
                                                <p className={`font-medium ${isSelected ? 'text-[#F39C12] dark:text-[#4478ea]' : 'text-black dark:text-white'
                                                    }`}>
                                                    {option.label}
                                                </p>
                                                <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                                                    {option.description}
                                                </p>
                                            </div>
                                             {isSelected && (
                                                <div className="w-5 h-5 rounded-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] flex items-center justify-center">
                                                    <div className="w-2 h-2 rounded-full bg-white" />
                                                </div>
                                             )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-black/10 dark:border-white/10 flex gap-3 bg-[#F5F5F5] dark:bg-[#2a2a2a]">
                            <Button
                                variant="outline"
                                onClick={handleReset}
                                className="flex-1 h-12 border-2 border-black/10 dark:border-white/10 text-black dark:text-white hover:border-[#F39C12] hover:bg-[#F39C12]/10 rounded-xl"
                            >
                                Reset
                            </Button>
                             <Button
                                 onClick={handleApply}
                                 className="flex-1 h-12 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white hover:opacity-90 rounded-xl"
                             >
                                Apply
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
}
